/**
 * NotificationItem Component
 * Individual notification with elegant animations and modern design
 */

import React, { useEffect, useState, useRef, useCallback } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { BaseComponentProps } from '@/shared/types/common.types'

import type { AppNotification } from './NotificationProvider'

interface NotificationItemProps extends BaseComponentProps {
  notification: AppNotification
  onDismiss: (id: string) => void
  index: number
  isFromBottom: boolean
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onDismiss,
  index,
  isFromBottom,
  testId,
}) => {
  // Include notification type in data-testid for E2E test targeting
  const resolvedTestId = testId ?? `toast-${notification.type}`
  const { t } = useTranslation('shared')
  const [isVisible, setIsVisible] = useState(false)
  const [isLeaving, setIsLeaving] = useState(false)
  const [progress, setProgress] = useState(100)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null)
  const leaveTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Memoized (and declared before the timer effect that depends on it) so the
  // effect only re-runs when the notification itself changes — a fresh function
  // identity each render would restart the auto-dismiss/progress timers.
  const handleDismiss = useCallback(() => {
    setIsLeaving(true)
    leaveTimerRef.current = setTimeout(() => {
      onDismiss(notification.id)
    }, 200)
  }, [notification.id, onDismiss])

  // A deduplicated refresh (`version` bump) that lands while the toast is already
  // leaving revives it: cancel the pending removal, or the refreshed toast would
  // vanish with the old timer.
  useEffect(() => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current)
      leaveTimerRef.current = null
    }
    setIsLeaving(false)
  }, [notification.version])

  useEffect(() => {
    // Show animation
    const showTimer = setTimeout(() => setIsVisible(true), 50 + index * 100)

    // A deduplicated refresh (`version` bump) re-runs this effect: start over.
    setProgress(100)

    // Progress bar animation and auto-dismiss
    if (notification.duration && notification.duration > 0) {
      const startTime = Date.now()
      const updateProgress = () => {
        const elapsed = Date.now() - startTime
        const remaining = Math.max(0, 100 - (elapsed / (notification.duration ?? 3000)) * 100)
        setProgress(remaining)

        if (remaining > 0) {
          progressTimerRef.current = setTimeout(updateProgress, 50)
        }
      }

      progressTimerRef.current = setTimeout(updateProgress, 50)

      timerRef.current = setTimeout(() => {
        handleDismiss()
      }, notification.duration)
    }

    return () => {
      clearTimeout(showTimer)
      if (timerRef.current) clearTimeout(timerRef.current)
      if (progressTimerRef.current) clearTimeout(progressTimerRef.current)
    }
  }, [notification.duration, notification.version, index, handleDismiss])

  // Hover-to-pause is attached to the DISMISS BUTTON only — see the
  // pointer-events comment on the root below. Hovering the button is the
  // deliberate "hold on, let me read/close this" gesture; a pointer that merely
  // happens to be where the toast appeared (e.g. right after clicking the button
  // the toast reports on) must NOT freeze the timer, which is exactly how the
  // post-import warning stayed on screen for minutes.
  const pauseTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (progressTimerRef.current) clearTimeout(progressTimerRef.current)
  }

  const resumeTimer = () => {
    if (notification.duration && notification.duration > 0 && progress > 0) {
      const remainingTime = (progress / 100) * notification.duration
      timerRef.current = setTimeout(handleDismiss, remainingTime)
    }
  }

  const handleAction = () => {
    notification.action?.onClick()
    handleDismiss()
  }

  const getTypeStyles = () => {
    switch (notification.type) {
      case 'success':
        return {
          bg: 'bg-crypto-success/10 border-crypto-success/20',
          text: 'text-crypto-success',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          ),
        }
      case 'error':
        return {
          bg: 'bg-crypto-danger/10 border-crypto-danger/20',
          text: 'text-crypto-danger',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ),
        }
      case 'warning':
        return {
          bg: 'bg-crypto-warning/10 border-crypto-warning/20',
          text: 'text-crypto-warning',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
        }
      case 'info':
      default:
        return {
          bg: 'bg-crypto-accent/10 border-crypto-accent/20',
          text: 'text-crypto-accent',
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
        }
    }
  }

  const typeStyles = getTypeStyles()

  const animationClass = isLeaving
    ? `opacity-0 scale-95 ${isFromBottom ? 'translate-y-2' : '-translate-y-2'}`
    : isVisible
      ? 'opacity-100 scale-100 translate-y-0'
      : `opacity-0 scale-95 ${isFromBottom ? 'translate-y-2' : '-translate-y-2'}`

  return (
    // The toast body is INERT. The container is already `pointer-events-none`,
    // but this element used to switch pointer events back on for the whole
    // subtree — so the toast (bottom-right, 320px wide) sat on top of the page
    // and swallowed clicks meant for whatever was underneath it: a real click on
    // the import action bar resolved to the toast's inner message div. Only the
    // dismiss button opts back in (below), which is the single control that has
    // to be clickable. Keeping the body inert ALSO fixes the toast that never
    // went away: with no mouseenter reaching it, a pointer resting where the
    // toast appeared can no longer pause the auto-dismiss timer.
    <div
      className={`
        pointer-events-none transform transition-all duration-200 ease-out
        ${animationClass}
      `}
      data-testid={resolvedTestId}
      // Every outcome reaches assistive tech, not just the bad ones. Errors and
      // warnings keep `alert` (interrupts); success/info get `status`, whose
      // implicit `aria-live="polite"` announces at the next pause. Before, they
      // carried NO role on a container with no `aria-live`, so a screen-reader
      // user could confirm an irreversible bulk write — the ticker rename says
      // "Esta acción no se puede deshacer" — and be told nothing about whether
      // it worked, while being told when it failed.
      role={notification.type === 'error' || notification.type === 'warning' ? 'alert' : 'status'}
      style={{
        transitionDelay: isVisible ? '0ms' : `${index * 100}ms`,
      }}
    >
      <div
        className={`
        relative overflow-hidden rounded-lg border backdrop-blur-sm shadow-lg
        ${typeStyles.bg} ${typeStyles.text}
        hover:shadow-xl transition-shadow duration-200
      `}
      >
        {/* Progress bar — only rendered when the toast auto-dismisses
            (`duration > 0`). The previous guard
            `notification.duration && notification.duration > 0` short-
            circuits to the literal `0` when `duration === 0` (the
            error-toast default — errors don't auto-dismiss), and JSX
            then rendered that `0` as a stray text node next to the
            real content. Coerce via `?? 0` so the falsy branch is
            always boolean. */}
        {(notification.duration ?? 0) > 0 && (
          <div
            className="absolute bottom-0 left-0 h-0.5 bg-current opacity-30 transition-all duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
        )}

        <div className="p-4 pr-10">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">{typeStyles.icon}</div>

            <div className="flex-1 min-w-0">
              {notification.title && (
                <div className="font-medium text-sm mb-1">{notification.title}</div>
              )}
              <div className="text-sm opacity-90 leading-relaxed">{notification.message}</div>
              {/* Optional rich content (issue #322 B.3 — post-sync resumen
                  toast). Rendered ONLY when caller supplied it — plain
                  string toasts stay visually unchanged. */}
              {notification.content && (
                <div className="mt-2 text-sm opacity-90 leading-relaxed">
                  {notification.content}
                </div>
              )}
              {notification.action && (
                <button
                  type="button"
                  data-testid="toast-action"
                  onClick={handleAction}
                  onMouseEnter={pauseTimer}
                  onMouseLeave={resumeTimer}
                  className="pointer-events-auto mt-2 inline-flex min-h-[44px] items-center rounded-md border border-crypto-border px-3 text-sm font-medium text-crypto-text hover:bg-crypto-border/40 sm:min-h-0 sm:py-1"
                >
                  {notification.action.label}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Close button */}
        {notification.dismissible && (
          <button
            onClick={handleDismiss}
            onMouseEnter={pauseTimer}
            onMouseLeave={resumeTimer}
            className="pointer-events-auto absolute top-2 right-2 p-1.5 rounded opacity-50 hover:opacity-100 transition-opacity"
            aria-label={t('notification.dismiss')}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}
